// Rechnungs-Reklamationen (Eltern ↔ Trainer)

import { getSupabaseClient } from "../supabase/index.js";
import {
  ATTENDANCE_DISPUTE_ACCEPT_REASON_CODES,
  ATTENDANCE_DISPUTE_CLAIM_REASON_CODES,
  ATTENDANCE_DISPUTE_REJECT_REASON_CODES,
} from "./attendance-disputes.js";
import { isParentPortalOpenStatement } from "./parent-portal-statements.js";

/** @readonly */
export const BILLING_STATEMENT_DISPUTE_CLAIM_REASON_CODES = Object.freeze([
  "wrong_amount",
  "wrong_duration",
  "duplicate_charge",
  "service_not_rendered",
  "wrong_assignment",
  "other",
]);

function __asStr(x) {
  return String(x ?? "").trim();
}

/** @type {boolean|null} */
let __parentPortalListDisputesRpcAvailable = null;

function __isRpcUnavailable(error, rpcName = "") {
  if (!error || typeof error !== "object") return false;
  const code = String(error.code || "");
  if (code === "PGRST202" || code === "42883") return true;
  const status = Number(error.status || error.statusCode || 0);
  if (status === 404) return true;
  const msg = String(error.message || error.details || error.hint || "").toLowerCase();
  const needle = __asStr(rpcName).toLowerCase();
  return (
    msg.includes("not found") ||
    msg.includes("could not find") ||
    (needle && msg.includes(needle)) ||
    msg.includes("trainerstunden_list_billing_statement_disputes") ||
    msg.includes("trainerstunden_parent_portal_list_statement_disputes")
  );
}

function __rpcJsonbArray(data) {
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
}

export { ATTENDANCE_DISPUTE_ACCEPT_REASON_CODES as BILLING_STATEMENT_DISPUTE_ACCEPT_REASON_CODES };
export { ATTENDANCE_DISPUTE_REJECT_REASON_CODES as BILLING_STATEMENT_DISPUTE_REJECT_REASON_CODES };

/**
 * Reklamation nur bei offenem Rechnungsbetrag (noch nicht bezahlt).
 *
 * @param {object|null|undefined} statement
 */
export function isBillingStatementDisputable(statement) {
  return isParentPortalOpenStatement(statement);
}

/**
 * @param {string} code
 * @returns {string} i18n key or empty
 */
export function billingStatementDisputeClaimReasonI18nKey(code) {
  const c = __asStr(code);
  if (!c) return "";
  if (BILLING_STATEMENT_DISPUTE_CLAIM_REASON_CODES.includes(c)) {
    return `trainerstunden.family.invoiceDispute.claimReason.${c}`;
  }
  if (ATTENDANCE_DISPUTE_CLAIM_REASON_CODES.includes(c)) {
    return `trainerstunden.family.dispute.claimReason.${c}`;
  }
  return "";
}

/**
 * @param {object|null|undefined} dispute
 */
export function normalizeBillingStatementDispute(dispute) {
  if (!dispute || typeof dispute !== "object") return null;
  const status = __asStr(dispute.status);
  if (!status) return null;
  return {
    id: __asStr(dispute.id),
    statement_id: __asStr(dispute.statement_id),
    statement_item_id: __asStr(dispute.statement_item_id) || null,
    person_id: __asStr(dispute.person_id),
    status,
    claim_reason_code: __asStr(dispute.claim_reason_code),
    trainer_response_reason_code: __asStr(dispute.trainer_response_reason_code),
    trainer_response_note: __asStr(dispute.trainer_response_note),
    student_acknowledged_at: dispute.student_acknowledged_at ?? null,
    submitted_at: dispute.submitted_at ?? null,
    resolved_at: dispute.resolved_at ?? null,
  };
}

/**
 * @param {Array<object>|null|undefined} disputes
 */
export function listPendingBillingStatementDisputes(disputes) {
  return (Array.isArray(disputes) ? disputes : [])
    .map((row) => normalizeBillingStatementDispute(row))
    .filter((row) => row && row.status === "pending");
}

/**
 * @param {string} statementId
 * @param {Array<object>|null|undefined} disputes
 */
export function statementHasPendingBillingDispute(statementId, disputes) {
  const sid = __asStr(statementId);
  if (!sid) return false;
  return listPendingBillingStatementDisputes(disputes).some((row) => row.statement_id === sid);
}

/**
 * @param {string} statementItemId
 * @param {Array<object>|null|undefined} disputes
 */
export function statementItemDisputeMode(statementItemId, disputes) {
  const iid = __asStr(statementItemId);
  if (!iid) return "none";
  const rows = (Array.isArray(disputes) ? disputes : [])
    .map((row) => normalizeBillingStatementDispute(row))
    .filter((row) => row && row.statement_item_id === iid);
  const pending = rows.find((r) => r.status === "pending");
  if (pending) return "pending";
  const latest = rows[0];
  if (latest?.status === "accepted") return "accepted";
  if (latest?.status === "rejected") return "rejected";
  return "submit";
}

/**
 * @param {string} statementId
 * @param {Array<object>|null|undefined} disputes
 */
export function wholeStatementDisputeMode(statementId, disputes) {
  const sid = __asStr(statementId);
  if (!sid) return "none";
  const rows = (Array.isArray(disputes) ? disputes : [])
    .map((row) => normalizeBillingStatementDispute(row))
    .filter((row) => row && row.statement_id === sid && !row.statement_item_id);
  const pending = rows.find((r) => r.status === "pending");
  if (pending) return "pending";
  const latest = rows[0];
  if (latest?.status === "accepted") return "accepted";
  if (latest?.status === "rejected") return "rejected";
  return "submit";
}

/**
 * @param {unknown} reason
 * @returns {string} i18n key
 */
export function resolveBillingStatementDisputeSubmitErrorKey(reason) {
  switch (__asStr(reason)) {
    case "statement-not-accessible":
    case "person-not-allowed":
      return "trainerstunden.family.booking.errors.notAllowed";
    case "statement-not-disputable":
      return "trainerstunden.family.invoiceDispute.errors.notDisputablePaid";
    case "dispute-already-pending":
      return "trainerstunden.family.invoiceDispute.errors.alreadyPending";
    case "item-not-found":
    case "statement-not-found":
    case "missing-params":
    case "invalid-reason":
      return "trainerstunden.family.invoiceDispute.errors.validation";
    default:
      return "trainerstunden.family.invoiceDispute.errors.failed";
  }
}

/**
 * @param {unknown} reason
 * @returns {string} i18n key
 */
export function resolveBillingStatementDisputeResolveErrorKey(reason) {
  switch (__asStr(reason)) {
    case "dispute-not-pending":
      return "trainerstunden.billingStatementDispute.resolve.errors.notPending";
    case "invalid-accept-reason":
    case "invalid-reject-reason":
    case "missing-params":
      return "trainerstunden.billingStatementDispute.resolve.errors.validation";
    default:
      return "trainerstunden.billingStatementDispute.resolve.errors.failed";
  }
}

export async function loadParentPortalStatementDisputesRemote(statementId) {
  const sid = __asStr(statementId);
  if (!sid) return { ok: true, data: [] };
  if (__parentPortalListDisputesRpcAvailable === false) {
    return { ok: true, data: [], reason: "rpc-missing" };
  }
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };
    const rpcName = "trainerstunden_parent_portal_list_statement_disputes";
    const { data, error } = await supabase.rpc(rpcName, {
      p_statement_id: sid,
    });
    if (error) {
      if (__isRpcUnavailable(error, rpcName)) {
        __parentPortalListDisputesRpcAvailable = false;
        return { ok: true, data: [], reason: "rpc-missing" };
      }
      return { ok: false, reason: "rpc-error", error };
    }
    __parentPortalListDisputesRpcAvailable = true;
    const rows = __rpcJsonbArray(data);
    return {
      ok: true,
      data: rows.map((row) => normalizeBillingStatementDispute(row)).filter(Boolean),
    };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

/**
 * @param {{ statementId: string, statementItemId?: string|null, personId?: string, claimReasonCode: string }} input
 */
export async function submitParentPortalBillingStatementDisputeRemote(input) {
  const statementId = __asStr(input?.statementId);
  const claimReasonCode = __asStr(input?.claimReasonCode);
  const statementItemId = __asStr(input?.statementItemId) || null;
  const personId = __asStr(input?.personId) || null;
  if (!statementId || !claimReasonCode) return { ok: false, reason: "missing-params" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };
    const { data, error } = await supabase.rpc("trainerstunden_parent_portal_submit_billing_statement_dispute", {
      p_statement_id: statementId,
      p_statement_item_id: statementItemId,
      p_person_id: personId,
      p_claim_reason_code: claimReasonCode,
    });
    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object") return { ok: false, reason: "no-data" };
    if (data.ok === false) return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true, dispute: normalizeBillingStatementDispute(data.dispute) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function loadBillingStatementDisputesRemote({ status = "pending" } = {}) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };
    const { data, error } = await supabase.rpc("trainerstunden_list_billing_statement_disputes", {
      p_status: __asStr(status) || "pending",
    });
    if (error) {
      if (__isRpcUnavailable(error, "trainerstunden_list_billing_statement_disputes")) {
        return { ok: true, data: [], reason: "rpc-missing" };
      }
      return { ok: false, reason: "rpc-error", error };
    }
    const rows = __rpcJsonbArray(data);
    return { ok: true, data: rows.map((row) => normalizeBillingStatementDispute(row)).filter(Boolean) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function resolveBillingStatementDisputeRemote({
  disputeId,
  accept,
  responseReasonCode,
  responseNote = "",
}) {
  const id = __asStr(disputeId);
  const reason = __asStr(responseReasonCode);
  if (!id || !reason) return { ok: false, reason: "missing-params" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };
    const { data, error } = await supabase.rpc("trainerstunden_resolve_billing_statement_dispute", {
      p_dispute_id: id,
      p_accept: Boolean(accept),
      p_response_reason_code: reason,
      p_response_note: String(responseNote ?? "").trim() || null,
    });
    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object") return { ok: false, reason: "no-data" };
    if (data.ok === false) return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true, dispute: normalizeBillingStatementDispute(data.dispute) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}
